import type { TFunction } from 'i18next'

import type { PlaybookContext } from '@/components/coach-chat-sheet'

export const PLAYBOOKS: Array<{ key: string; goalPrompt: string }> = [
  {
    key: 'parkinsonsLaw',
    goalPrompt:
      "Apply Parkinson's Law to this goal. Call list_goal_tasks to see its incomplete tasks, then call propose_task_deadlines with a tight artificial due date for each. Base each due date on the task's difficulty AND its milestone phase: within a milestone use 1-2 days for quick tasks, 3-5 for medium, up to 7 for hard — then push each later milestone's tasks further out so earlier phases come due before later ones. After calling propose_task_deadlines, write a single short sentence only.",
  },
  {
    key: 'pomodoro',
    goalPrompt:
      "Apply the Pomodoro technique to this goal. Call list_goal_tasks to see its tasks. For each incomplete task, estimate how many 25-min Pomodoros it needs. Group them into a concrete session plan for this week — e.g. 'Monday: 2 Pomodoros on X, then 1 on Y'. Give me a ready-to-follow schedule.",
  },
  {
    key: 'twoMinuteRule',
    goalPrompt:
      'Apply the 2-Minute Rule to this goal. Call list_goal_tasks to see its tasks. Identify every incomplete task I could realistically knock out in under 2 minutes right now. List them clearly so I can act on them immediately — no excuses.',
  },
  {
    key: 'eisenhower',
    goalPrompt:
      "Apply the Eisenhower Matrix to this goal and ACT on it — don't just describe it. Call list_goal_tasks, sort every incomplete task into a quadrant, and take the matching action with tools: Q1 Urgent & Important → call set_task_urgent on each (do now). Q2 Important but Not Urgent → call set_task_deadline on each to schedule it, staggering the due dates across the next 1–3 weeks (never give them all the same date). Q3 Urgent but Not Important → these aren't your priority; briefly note them as candidates to hand off or knock out fast, take no destructive action. Q4 Neither → list these as elimination candidates and ASK me to confirm before removing anything; only call delete_tasks after I say yes. Finish with a short summary grouped by quadrant: what you marked urgent, what you scheduled (with dates), and what's waiting on my OK to delete.",
  },
  {
    key: 'timeBlocking',
    goalPrompt:
      'Apply Time-blocking to this goal. Call list_goal_tasks to see what needs doing. Suggest how to batch these tasks into focused calendar blocks this week — which tasks to group together, what time of day suits each, and how long to block. Give me a concrete day-by-day schedule I can drop straight into my calendar.',
  },
  {
    key: 'habitStacking',
    goalPrompt:
      'Apply Habit Stacking to this goal. Call list_goal_tasks to understand what the work involves. Then suggest 2-3 specific habit stacks — existing daily routines I likely already do (morning coffee, brushing teeth, commute) that I can anchor a task or practice from this goal onto. Make the stacks concrete and immediately actionable.',
  },
  {
    key: 'flowState',
    goalPrompt:
      'Apply Flow State principles to this goal. Call list_goal_tasks to see the tasks. Identify the 1-2 tasks that sit in the sweet spot between too-easy and too-hard for where I am right now. Recommend the ideal session length, what distractions to cut, and a single focus target for my next deep work block.',
  },
  {
    key: 'weeklyReview',
    goalPrompt:
      'Do a weekly review of this goal. Call list_goal_tasks to see completed and pending tasks. Give me three things: (1) wins from this goal this week, (2) anything overdue or stuck and why, (3) the top 3 tasks to focus on next week, in priority order. Keep it sharp — this should take 5 minutes to read.',
  },
  {
    key: 'breakItDown',
    goalPrompt:
      "Break down this goal into the smallest possible next steps. Call list_goal_tasks to see the current structure. Find any tasks that are still too vague or large to act on today and suggest how to split each one into 2-3 concrete sub-actions. Also flag any gaps — things that need to be done but aren't captured yet.",
  },
]

export function buildPlaybookContext(
  t: TFunction,
  key: string,
  goalPrompt: string,
): PlaybookContext {
  return {
    title: t(`discover.playbook.${key}.title`),
    hook: t(`discover.playbook.${key}.hook`),
    goalPrompt,
  }
}
